// Keyboard shortcuts help overlay triggered by pressing '?'

import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Shortcut } from '@/hooks/useKeyboardShortcuts';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: Shortcut[];
}

function formatKeys(keys: string | string[]): string {
  const keyLabels: Record<string, string> = {
    ArrowLeft: '\u2190',
    ArrowRight: '\u2192',
    ArrowUp: '\u2191',
    ArrowDown: '\u2193',
    Escape: 'Esc',
    Enter: '\u23CE',
  };

  if (Array.isArray(keys)) {
    return keys.map((k) => keyLabels[k] ?? k).join(' \u2192 ');
  }
  return keyLabels[keys] ?? keys;
}

export function KeyboardShortcutsHelp({ isOpen, onClose, shortcuts }: KeyboardShortcutsHelpProps) {
  const { t } = useTranslation('common');

  // Group shortcuts by their group property
  const groups = new Map<string, Shortcut[]>();
  for (const shortcut of shortcuts) {
    const existing = groups.get(shortcut.group) ?? [];
    existing.push(shortcut);
    groups.set(shortcut.group, existing);
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('keyboard.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {Array.from(groups.entries()).map(([group, items]) => (
            <div key={group}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {group}
              </h3>
              <div className="space-y-2">
                {items.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-sm text-foreground">{shortcut.description}</span>
                    <kbd className="ml-4 px-2 py-1 text-xs font-mono bg-muted border border-border rounded shrink-0">
                      {formatKeys(shortcut.keys)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
